import { useCallback } from "react";
import {
  Alignment,
  Content,
  ItemType,
  Stroke,
  TextConfiguration,
  TextItem,
  TrackType,
} from "../../../types";
import { useTimelineContext } from "../../remotion-timeline/context/use-timeline-context";
import {
  DEFAULT_ROTATION,
  DEFAULT_TEXT_CONFIGURATION,
} from "../../canvas/config";

const generateRandomId = () => `id_${Math.random().toString(36).substr(2, 9)}`;

export const useTextItemSettings = ({
  applyToAll,
  selectedItemObject,
}: any) => {
  const {
    addItem,
    fps,
    tracks,
    addTrack,
    setSelectedItem,
    selectedItem,
    changeItem,
  } = useTimelineContext();

  const addTextItem = useCallback(
    ({ startTimeInFrames }: { startTimeInFrames: number }) => {
      if (!fps || !addItem || !addTrack || !tracks) {
        console.error("Timeline context functions or tracks not available.");
        return;
      }

      const DURATION_IN_SECONDS = 3;
      const durationInFrames = DURATION_IN_SECONDS * fps;

      const newTextItem: TextItem = {
        id: generateRandomId(),
        from: startTimeInFrames,
        durationInFrames,
        isDragging: false,
        type: "text",
        text: "New Text",
        position: { x: 0.1, y: 0.8 },
        rotation: DEFAULT_ROTATION,
        size: { width: 400, height: 100 },
        configuration: DEFAULT_TEXT_CONFIGURATION,
      };

      for (let i = tracks.length - 1; i >= 0; i--) {
        const currentTrack = tracks[i];

        const hasOverlap = currentTrack.items.some((item: ItemType) => {
          const newStart = newTextItem.from;
          const newEnd = newTextItem.from + newTextItem.durationInFrames;
          const existingStart = item.from;
          const existingEnd = item.from + item.durationInFrames;

          const startsInside =
            newStart >= existingStart && newStart < existingEnd;
          const endsInside = newEnd > existingStart && newEnd <= existingEnd;
          const envelops = newStart <= existingStart && newEnd >= existingEnd;

          return startsInside || endsInside || envelops;
        });

        if (!hasOverlap) {
          addItem(i, newTextItem);
          setSelectedItem?.(newTextItem.id);
          return;
        }
      }

      const newTrack: TrackType = {
        id: generateRandomId(),
        name: `Spor ${tracks.length + 1}`,
        items: [],
      };
      addTrack(newTrack);

      setTimeout(() => {
        addItem(0, newTextItem);
        setSelectedItem?.(newTextItem.id);
      }, 100);
    },
    [tracks, addItem, addTrack, fps, setSelectedItem]
  );

  const updateGoogleFontsLink = useCallback(
    (selectedFont: { family: string }) => {
      if (!selectedFont?.family) return;

      const linkElement = document.getElementById(
        "editor-preview-fonts-link"
      ) as HTMLLinkElement;

      if (linkElement) {
        linkElement.href = `https://fonts.googleapis.com/css2?family=${selectedFont.family.replace(
          / /g,
          "+"
        )}&display=swap`;
      } else {
        const newLink = document.createElement("link");
        newLink.id = "editor-preview-fonts-link";
        newLink.rel = "stylesheet";
        newLink.href = `https://fonts.googleapis.com/css2?family=${selectedFont.family.replace(
          / /g,
          "+"
        )}&display=swap`;
        document.head.appendChild(newLink);
      }
    },
    []
  );

  const updateFontSettings = useCallback(
    (fontSettings: Partial<TextConfiguration["font"]>) => {
      if (selectedItemObject?.type !== "text") return;

      if (fontSettings.family) {
        updateGoogleFontsLink({ family: fontSettings.family });
      }

      const updateLogic = (item: TextItem): TextItem => ({
        ...item,
        configuration: {
          ...item.configuration,
          font: { ...item.configuration.font, ...fontSettings },
        },
      });

      if (applyToAll) {
        tracks.forEach((track: any) =>
          track.items.forEach((item: any) => {
            if (item.type === "text")
              changeItem(item.id, (i: any) => updateLogic(i as TextItem));
          })
        );
      } else {
        changeItem(selectedItem!, (i: any) => updateLogic(i as TextItem));
      }
    },
    [
      selectedItem,
      selectedItemObject,
      applyToAll,
      tracks,
      changeItem,
      updateGoogleFontsLink,
    ]
  );

  const updateAlignmentSettings = useCallback(
    (alignmentUpdate: Partial<Alignment>) => {
      if (selectedItemObject?.type !== "text") return;

      const updateLogic = (item: TextItem): TextItem => ({
        ...item,
        configuration: {
          ...item.configuration,
          alignment: { ...item.configuration.alignment, ...alignmentUpdate },
        },
      });

      if (applyToAll) {
        tracks.forEach((track: any) =>
          track.items.forEach((item: any) => {
            if (item.type === "text")
              changeItem(item.id, (i: any) => updateLogic(i as TextItem));
          })
        );
      } else {
        changeItem(selectedItem!, (i: any) => updateLogic(i as TextItem));
      }
    },
    [selectedItem, selectedItemObject, applyToAll, tracks, changeItem]
  );

  const updateStrokeSettings = useCallback(
    (strokeInput: Partial<Stroke>) => {
      if (selectedItemObject?.type !== "text") return;

      const updateLogic = (item: TextItem): TextItem => {
        const newStroke: Stroke =
          strokeInput === null
            ? null
            : {
                width:
                  strokeInput?.width ?? item.configuration.stroke?.width ?? 0,
                color:
                  strokeInput?.color ??
                  item.configuration.stroke?.color ??
                  "transparent",
              };

        return {
          ...item,
          configuration: { ...item.configuration, stroke: newStroke },
        };
      };

      if (applyToAll) {
        tracks.forEach((track: any) =>
          track.items.forEach((item: any) => {
            if (item.type === "text")
              changeItem(item.id, (i: any) => updateLogic(i as TextItem));
          })
        );
      } else {
        changeItem(selectedItem!, (i: any) => updateLogic(i as TextItem));
      }
    },
    [selectedItem, selectedItemObject, applyToAll, tracks, changeItem]
  );

  const updateContentSettings = useCallback(
    (content: Partial<Content>) => {
      if (selectedItemObject?.type !== "text") return;

      changeItem(selectedItem!, (item: any) => {
        const textItem = item as TextItem;
        return {
          ...textItem,
          text: content.text ?? textItem.text,
        };
      });
    },
    [selectedItem, selectedItemObject, changeItem]
  );

  return {
    updateFontSettings,
    updateAlignmentSettings,
    updateStrokeSettings,
    updateContentSettings,
    addTextItem,
  };
};
